/**
 * FI Staff Lifecycle Hooks (Sprint 5 S5-A)
 *
 * React Query wrappers for the canonical FI Staff Authn/Z lifecycle endpoints
 * shipped by aaim-core authorization (Sprints 1-4). Surfaces invitations,
 * staff list/detail, status transitions (suspend / reactivate / offboard),
 * and role updates with effective-permission preview.
 *
 * Types derived from fi-staff-lifecycle.openapi.yaml via
 * @aaimplatform/institutional-types.
 *
 * Sprint 1 schema deviations honored:
 * - Invitation.roleSlug is a single VARCHAR (single-role-per-invite).
 * - Multi-role grants happen post-acceptance via updateStaffRoles.
 *
 * Sensitive operations (suspend, reactivate, offboard, credential reset,
 * fi_admin role grants) require an X-Step-Up-Token header. Sprint 6 ships the step-up mint flow; until then
 * callers can pass `stepUpToken` explicitly through the mutation variables
 * and the hook propagates it to the request header. Without a token, the
 * server returns 403 STEP_UP_REQUIRED and the UI must trigger the step-up
 * modal.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authStepUpClient, authTotpClient, fiStaffLifecycleClient } from '../api/client';
import type { components } from '@aaimplatform/institutional-types/fi-staff-lifecycle';
import type { components as AuthStepUpComponents } from '@aaimplatform/institutional-types/auth-step-up';
import type { components as AuthTotpComponents } from '@aaimplatform/institutional-types/auth-totp';

// ---------------------------------------------------------------------------
// Type aliases from generated OpenAPI schema
// ---------------------------------------------------------------------------

export type InvitationStatus = components['schemas']['InvitationStatus'];
export type TenantUserStatus = components['schemas']['TenantUserStatus'];
export type Invitation = components['schemas']['Invitation'];
export type InviteStaffRequest = components['schemas']['InviteStaffRequest'];
export type InviteStaffResponse = components['schemas']['InviteStaffResponse'];
export type InvitationListResponse = components['schemas']['InvitationListResponse'];
// Wave 3.5 corrective (2026-05-27): the accept-invitation operation
// moved to schema-registry/auth-totp.openapi.yaml so the credential-path
// password field + credentialPathEnabled response discriminator can ride
// with the rest of the credential-auth flow (login, TOTP enroll/verify/
// challenge). The fi-staff-lifecycle spec no longer declares these.
export type AcceptInvitationRequest = AuthTotpComponents['schemas']['AcceptInvitationRequest'];
export type AcceptInvitationResponse = AuthTotpComponents['schemas']['AcceptInvitationResponse'];
export type Staff = components['schemas']['Staff'];
export type StaffListResponse = components['schemas']['StaffListResponse'];
export type RoleRef = components['schemas']['RoleRef'];
export type SuspendStaffRequest = components['schemas']['SuspendStaffRequest'];
export type ReactivateStaffRequest = components['schemas']['ReactivateStaffRequest'];
export type OffboardStaffRequest = components['schemas']['OffboardStaffRequest'];
export type ResetStaffCredentialRequest = components['schemas']['ResetStaffCredentialRequest'];
export type ResetStaffCredentialResponse = components['schemas']['ResetStaffCredentialResponse'];
export type UpdateStaffRolesRequest = components['schemas']['UpdateStaffRolesRequest'];
export type UpdateStaffRolesResponse = components['schemas']['UpdateStaffRolesResponse'];
export type EffectivePermissionsSet = components['schemas']['EffectivePermissionsSet'];
export type EffectivePermissionsDiff = components['schemas']['EffectivePermissionsDiff'];
export type AbacPredicateRef = components['schemas']['AbacPredicateRef'];
// GH #2317: the staff audit tail (fi-staff-lifecycle 1.3.0 listStaffAuditEvents).
export type StaffAuditAction = components['schemas']['StaffAuditAction'];
export type StaffAuditEvent = components['schemas']['StaffAuditEvent'];
export type StaffAuditEventDetails = components['schemas']['StaffAuditEventDetails'];
export type StaffAuditEventListResponse = components['schemas']['StaffAuditEventListResponse'];

// Sprint 6: step-up token mint types (generated from auth-step-up.openapi.yaml)
export type StepUpOperation = AuthStepUpComponents['schemas']['StepUpOperation'];
export type StepUpProofType = AuthStepUpComponents['schemas']['StepUpProofType'];
export type StepUpProof = AuthStepUpComponents['schemas']['StepUpProof'];
export type MintStepUpTokenRequest = AuthStepUpComponents['schemas']['MintStepUpTokenRequest'];
export type MintStepUpTokenResponse = AuthStepUpComponents['schemas']['MintStepUpTokenResponse'];

/**
 * Error class surfaced when a sensitive mutation returns 403
 * STEP_UP_REQUIRED. The UI catches this to open the step-up modal,
 * mint a token via `useStepUpToken`, and retry the original mutation
 * with the X-Step-Up-Token header populated.
 */
export class StepUpRequiredError extends Error {
  constructor(public readonly errorCode: string = 'STEP_UP_REQUIRED', message?: string) {
    super(message ?? 'Step-up authentication required');
    this.name = 'StepUpRequiredError';
  }
}

/**
 * An error carrying the HTTP status and the server's machine-readable error
 * code, so a caller can classify a failure without parsing prose.
 *
 * GH #2461 deliverable 3. `buildErrorFromResult` previously returned a bare
 * `Error` holding only the message, which left every caller with one option:
 * render `err.message`. That is how a browser-internal TypeError
 * ("Failed to execute 'clone' on 'Request'") came to be shown to an operator
 * under the heading "Verification failed".
 *
 * A subclass of Error, so every existing `err.message` consumer is unchanged.
 */
export class ApiResponseError extends Error {
  constructor(
    public readonly status: number,
    public readonly errorCode: string | undefined,
    message: string,
    /**
     * Parsed `Retry-After` in seconds, when the server sent one. GH #2659:
     * the step-up mint refuses a brute-forced proof with 429 + Retry-After,
     * and without this the modal has to invent a wait to show the operator.
     */
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiResponseError';
  }
}

/**
 * Read `Retry-After` off a response. RFC 9110 allows either delay-seconds or
 * an HTTP-date; Aaim services send seconds, and the date branch is here so a
 * proxy or CDN that rewrites it does not silently produce NaN.
 *
 * Returns undefined when the header is absent, unparseable, or already past --
 * the caller then says "later" rather than a fabricated number.
 */
function parseRetryAfter(response: Response | undefined): number | undefined {
  const raw = response?.headers?.get('Retry-After');
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number(trimmed);
    return seconds > 0 ? seconds : undefined;
  }
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return undefined;
  const seconds = Math.ceil((at - Date.now()) / 1000);
  return seconds > 0 ? seconds : undefined;
}

function buildErrorFromResult<T>(result: FetchResult<T>): Error {
  const status = result.response?.status ?? 0;
  const statusText = result.response?.statusText ?? 'Unknown';
  const errorBody = result.error as { message?: string; error?: string } | undefined;
  const code = errorBody?.error;
  const message = errorBody?.message ?? code ?? `HTTP ${status}: ${statusText}`;
  if (status === 403 && (code === 'STEP_UP_REQUIRED' || code === 'STEP_UP_EXPIRED')) {
    return new StepUpRequiredError(code, message);
  }
  return new ApiResponseError(status, code, message, parseRetryAfter(result.response));
}

// ---------------------------------------------------------------------------
// Query key constants
// ---------------------------------------------------------------------------

const STAFF_KEY = 'fi-staff';
const INVITATIONS_KEY = 'fi-staff-invitations';

// ---------------------------------------------------------------------------
// API helpers -- thin wrappers over the typed openapi-fetch client
// ---------------------------------------------------------------------------

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

function unwrap<T>(result: FetchResult<T>): T {
  if (result.error || result.data === undefined) {
    throw buildErrorFromResult(result);
  }
  return result.data;
}

function unwrapVoid<T>(result: FetchResult<T>): void {
  if (result.error) {
    throw buildErrorFromResult(result);
  }
}

const staffApi = {
  // ---- Invitations ----

  listInvitations: async (params?: {
    status?: InvitationStatus;
  }): Promise<InvitationListResponse> => {
    const result = await fiStaffLifecycleClient.GET('/api/v1/admin/staff/invitations', {
      params: { query: { status: params?.status } },
    });
    return unwrap(result);
  },

  invite: async (body: InviteStaffRequest): Promise<InviteStaffResponse> => {
    const result = await fiStaffLifecycleClient.POST('/api/v1/admin/staff/invitations', {
      body,
    });
    return unwrap(result);
  },

  revokeInvitation: async (invitationId: string): Promise<void> => {
    const result = await fiStaffLifecycleClient.DELETE(
      '/api/v1/admin/staff/invitations/{invitationId}',
      { params: { path: { invitationId } } },
    );
    unwrapVoid(result);
  },

  resendInvitation: async (invitationId: string): Promise<void> => {
    const result = await fiStaffLifecycleClient.POST(
      '/api/v1/admin/staff/invitations/{invitationId}/resend',
      { params: { path: { invitationId } } },
    );
    unwrapVoid(result);
  },

  acceptInvitation: async (
    body: AcceptInvitationRequest,
  ): Promise<AcceptInvitationResponse> => {
    // Canonical credential-path accept-invitation endpoint per ADR-008.
    // Anonymous: the plaintextToken in body authenticates the request;
    // the password field is required when the resolved tenant has
    // credentialAuthEnabled=true and ignored otherwise. Which tenants carry
    // the flag is tenant data; .claude/rules/prod-qa-operator-portals.md
    // lists the production set. NDFCU is SSO-only and not among them.
    const result = await authTotpClient.POST(
      '/api/v1/auth/invitations/accept',
      { body },
    );
    return unwrap(result);
  },

  // ---- Staff ----

  listStaff: async (params?: {
    status?: TenantUserStatus;
    page?: number;
    pageSize?: number;
  }): Promise<StaffListResponse> => {
    const result = await fiStaffLifecycleClient.GET('/api/v1/admin/staff', {
      params: {
        query: {
          status: params?.status,
          page: params?.page,
          pageSize: params?.pageSize,
        },
      },
    });
    return unwrap(result);
  },

  getStaff: async (userId: string): Promise<Staff> => {
    const result = await fiStaffLifecycleClient.GET('/api/v1/admin/staff/{userId}', {
      params: { path: { userId } },
    });
    return unwrap(result);
  },

  listStaffAuditEvents: async (
    userId: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<StaffAuditEventListResponse> => {
    const result = await fiStaffLifecycleClient.GET(
      '/api/v1/admin/staff/{userId}/audit-events',
      {
        params: {
          path: { userId },
          query: { cursor: params?.cursor, limit: params?.limit },
        },
      },
    );
    return unwrap(result);
  },

  suspendStaff: async (
    userId: string,
    body: SuspendStaffRequest,
    stepUpToken?: string,
  ): Promise<void> => {
    const result = await fiStaffLifecycleClient.POST(
      '/api/v1/admin/staff/{userId}/suspend',
      {
        params: {
          path: { userId },
          header: stepUpToken ? { 'X-Step-Up-Token': stepUpToken } : undefined,
        },
        body,
      },
    );
    unwrapVoid(result);
  },

  reactivateStaff: async (
    userId: string,
    body: ReactivateStaffRequest,
    stepUpToken?: string,
  ): Promise<void> => {
    const result = await fiStaffLifecycleClient.POST(
      '/api/v1/admin/staff/{userId}/reactivate',
      {
        params: {
          path: { userId },
          header: stepUpToken ? { 'X-Step-Up-Token': stepUpToken } : undefined,
        },
        body,
      },
    );
    unwrapVoid(result);
  },

  offboardStaff: async (
    userId: string,
    body: OffboardStaffRequest,
    stepUpToken?: string,
  ): Promise<void> => {
    const result = await fiStaffLifecycleClient.POST(
      '/api/v1/admin/staff/{userId}/offboard',
      {
        params: {
          path: { userId },
          header: stepUpToken ? { 'X-Step-Up-Token': stepUpToken } : undefined,
        },
        body,
      },
    );
    unwrapVoid(result);
  },

  resetStaffCredential: async (
    userId: string,
    body: ResetStaffCredentialRequest,
    stepUpToken?: string,
  ): Promise<ResetStaffCredentialResponse> => {
    const result = await fiStaffLifecycleClient.POST(
      '/api/v1/admin/staff/{userId}/reset-credential',
      {
        params: {
          path: { userId },
          header: stepUpToken ? { 'X-Step-Up-Token': stepUpToken } : undefined,
        },
        body,
      },
    );
    return unwrap(result);
  },

  updateStaffRoles: async (
    userId: string,
    body: UpdateStaffRolesRequest,
    stepUpToken?: string,
  ): Promise<UpdateStaffRolesResponse> => {
    const result = await fiStaffLifecycleClient.PUT(
      '/api/v1/admin/staff/{userId}/roles',
      {
        params: {
          path: { userId },
          header: stepUpToken ? { 'X-Step-Up-Token': stepUpToken } : undefined,
        },
        body,
      },
    );
    return unwrap(result);
  },
};

// ---------------------------------------------------------------------------
// Step-up token mint hook (Sprint 6)
// ---------------------------------------------------------------------------

const authStepUpApi = {
  mintStepUpToken: async (body: MintStepUpTokenRequest): Promise<MintStepUpTokenResponse> => {
    const result = await authStepUpClient.POST('/api/v1/auth/step-up-token', { body });
    return unwrap(result);
  },
};

/**
 * Mint a step-up token bound to a single sensitive operation. The
 * resulting token is sent as the X-Step-Up-Token header on the follow-up
 * mutation; the upstream guard verifies the JWT and matches its
 * stepUpOpHash claim against the request shape.
 *
 * The mutation function is mostly stateless: the StepUpAuthModal calls
 * mutateAsync directly, caches the returned token by operationHash, and
 * passes the token to the parent component which retries the original
 * mutation.
 */
export function useStepUpToken() {
  return useMutation<MintStepUpTokenResponse, Error, MintStepUpTokenRequest>({
    mutationFn: (body) => authStepUpApi.mintStepUpToken(body),
  });
}

// ---------------------------------------------------------------------------
// Invitation hooks
// ---------------------------------------------------------------------------

/**
 * List invitations for the calling tenant. Optional status filter.
 */
export function useInvitations(params?: { status?: InvitationStatus }) {
  return useQuery<InvitationListResponse, Error>({
    queryKey: [INVITATIONS_KEY, 'list', params],
    queryFn: () => staffApi.listInvitations(params),
  });
}

/**
 * Invite a staff member. Single-role per Sprint 1 schema; further roles
 * are granted after acceptance via useUpdateStaffRoles.
 */
export function useInviteStaff() {
  const queryClient = useQueryClient();

  return useMutation<InviteStaffResponse, Error, InviteStaffRequest>({
    mutationFn: (body) => staffApi.invite(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [INVITATIONS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [STAFF_KEY] });
    },
  });
}

/**
 * Revoke a PENDING invitation. Returns 409 if the invitation is already
 * ACCEPTED, REVOKED, or EXPIRED.
 */
export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { invitationId: string }>({
    mutationFn: ({ invitationId }) => staffApi.revokeInvitation(invitationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [INVITATIONS_KEY] });
    },
  });
}

/**
 * Re-queue the email for a PENDING invitation. Acceptance token is
 * unchanged; only PENDING invitations may be resent.
 */
export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { invitationId: string }>({
    mutationFn: ({ invitationId }) => staffApi.resendInvitation(invitationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [INVITATIONS_KEY] });
    },
  });
}

/**
 * Accept an invitation using the emailed token. Anonymous endpoint; the
 * invitee posts the plaintext token (and a password when the resolved
 * tenant has credentialAuthEnabled=true per ADR-008).
 *
 * The response carries a redirect URL plus the credentialPathEnabled
 * discriminator: true when an AuthCredential row was upserted,
 * false for SSO-managed tenants.
 */
export function useAcceptInvitation() {
  return useMutation<AcceptInvitationResponse, Error, AcceptInvitationRequest>({
    mutationFn: (body) => staffApi.acceptInvitation(body),
  });
}

// ---------------------------------------------------------------------------
// Staff hooks
// ---------------------------------------------------------------------------

/**
 * List staff in the calling tenant. Filter by TenantUserStatus.
 */
export function useStaff(params?: {
  status?: TenantUserStatus;
  page?: number;
  pageSize?: number;
}) {
  return useQuery<StaffListResponse, Error>({
    queryKey: [STAFF_KEY, 'list', params],
    queryFn: () => staffApi.listStaff(params),
  });
}

/**
 * Get a single staff member by user ID. Tenant-isolated.
 */
export function useStaffMember(userId: string | undefined) {
  return useQuery<Staff, Error>({
    queryKey: [STAFF_KEY, 'detail', userId],
    queryFn: () => staffApi.getStaff(userId as string),
    enabled: !!userId,
  });
}

/** Page size the StaffDetail audit card reads (the spec caps `limit` at 100). */
export const STAFF_AUDIT_PAGE_SIZE = 20;

/**
 * The audit events recorded against one staff member, newest first, one page
 * at a time (GH #2317). Keyed under the member's detail key, so each lifecycle
 * mutation that invalidates the detail refetches the tail with it.
 */
export function useStaffAuditEvents(userId: string | undefined) {
  return useInfiniteQuery({
    queryKey: [STAFF_KEY, 'detail', userId, 'audit-events'],
    queryFn: ({ pageParam }) =>
      staffApi.listStaffAuditEvents(userId as string, {
        cursor: pageParam ?? undefined,
        limit: STAFF_AUDIT_PAGE_SIZE,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!userId,
  });
}

/**
 * Suspend a staff member. Sensitive operation requiring step-up token.
 */
export function useSuspendStaff() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { userId: string; body: SuspendStaffRequest; stepUpToken?: string }
  >({
    mutationFn: ({ userId, body, stepUpToken }) =>
      staffApi.suspendStaff(userId, body, stepUpToken),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [STAFF_KEY, 'list'] });
      void queryClient.invalidateQueries({
        queryKey: [STAFF_KEY, 'detail', variables.userId],
      });
      // A suspend revokes the member's pending invitations on the server, so
      // the outstanding-invitations list is stale until it is read again.
      void queryClient.invalidateQueries({ queryKey: [INVITATIONS_KEY] });
    },
  });
}

/**
 * Reactivate a SUSPENDED staff member. Sensitive operation requiring a
 * step-up token (fi-staff-lifecycle 1.2.0 declares the header the server
 * already requires).
 */
export function useReactivateStaff() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { userId: string; body: ReactivateStaffRequest; stepUpToken?: string }
  >({
    mutationFn: ({ userId, body, stepUpToken }) =>
      staffApi.reactivateStaff(userId, body, stepUpToken),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [STAFF_KEY, 'list'] });
      void queryClient.invalidateQueries({
        queryKey: [STAFF_KEY, 'detail', variables.userId],
      });
    },
  });
}

/**
 * Off-board a staff member. Sensitive operation requiring step-up token.
 * Last-admin invariant prevents off-boarding the final fi_admin.
 */
export function useOffboardStaff() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { userId: string; body: OffboardStaffRequest; stepUpToken?: string }
  >({
    mutationFn: ({ userId, body, stepUpToken }) =>
      staffApi.offboardStaff(userId, body, stepUpToken),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [STAFF_KEY, 'list'] });
      void queryClient.invalidateQueries({
        queryKey: [STAFF_KEY, 'detail', variables.userId],
      });
      // An offboard revokes the member's pending invitations on the server.
      void queryClient.invalidateQueries({ queryKey: [INVITATIONS_KEY] });
    },
  });
}

/**
 * Reset a credential-path staff member's sign-in credential. Sensitive
 * operation requiring a step-up token, mirroring useSuspendStaff.
 *
 * The server deactivates the staff member's Aaim-managed password
 * credential, issues a single-use reset invitation (emailed, never
 * returned in the body), and revokes live sessions. Returns the
 * invitation id + expiry so the caller can surface the recovery window.
 *
 * Only meaningful when the staff payload reports
 * `credentialPathEnabled === true`; SSO-only tenants have no
 * Aaim-managed password and the server returns 409.
 */
export function useResetStaffCredential() {
  const queryClient = useQueryClient();

  return useMutation<
    ResetStaffCredentialResponse,
    Error,
    { userId: string; body: ResetStaffCredentialRequest; stepUpToken?: string }
  >({
    mutationFn: ({ userId, body, stepUpToken }) =>
      staffApi.resetStaffCredential(userId, body, stepUpToken),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [STAFF_KEY, 'list'] });
      void queryClient.invalidateQueries({
        queryKey: [STAFF_KEY, 'detail', variables.userId],
      });
      // A reset revokes the member's earlier links and issues a new one.
      void queryClient.invalidateQueries({ queryKey: [INVITATIONS_KEY] });
    },
  });
}

/**
 * Replace a staff member's role set. Server diffs against current roles
 * and emits STAFF_ROLE_GRANTED / STAFF_ROLE_REVOKED audit events per
 * delta. Returns the recomputed effective permission set. Granting
 * fi_admin requires staff:grant_admin permission AND a step-up token.
 */
export function useUpdateStaffRoles() {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateStaffRolesResponse,
    Error,
    { userId: string; body: UpdateStaffRolesRequest; stepUpToken?: string }
  >({
    mutationFn: ({ userId, body, stepUpToken }) =>
      staffApi.updateStaffRoles(userId, body, stepUpToken),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [STAFF_KEY, 'list'] });
      void queryClient.invalidateQueries({
        queryKey: [STAFF_KEY, 'detail', variables.userId],
      });
    },
  });
}
